import Header from "../components/header";
import mainStore from "../../store/store";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/footer";
import Stepper from "../components/stepper";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { SkeletonTopCars } from "./skeleton";
import AvailabilityModal from "../../components/AvailabilityModal";
import axiosInstance from "../../axios-interceptor/axios";
import axios from "axios";
import { useCMSContent } from "../../hooks/useCMSContent";
import "../../styles/carCards.css";

const TopCars = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedCarForAvailability, setSelectedCarForAvailability] = useState(null);
  const [availabilityData, setAvailabilityData] = useState([]);
  
  // CMS Content Hook - fetches content from API
  const { getSectionContent } = useCMSContent('home');

  const {
    getAllCars,
    userBooking,
    setUserBooking,
    visitedRoutes,
    setVisitedRoutes,
    setSelectedRoute,
    getCarImg,
    cachedCars,
    setCachedCars,
  } = mainStore();

  const routeTo = (routeTo) => {
    setSelectedRoute(routeTo);
    navigate(routeTo);
  };
  // Use cached data if available — no skeleton on revisit
  const [allCarsList, setAllCarsList] = useState(cachedCars.length > 0 ? cachedCars : []);
  const [loading, setLoading] = useState(cachedCars.length === 0);
  //   const carBookingCounts = futureBookings.reduce((acc, booking) => {
  //     acc[booking.car] = (acc[booking.car] || 0) + 1;
  //     return acc;
  //   }, {});
  //  const top3Cars = Object.entries(carBookingCounts)
  //   .sort((a, b) => b[1] - a[1]) // Sort by count descending
  //   .slice(0, 3) // Get top 3
  //   .map(([car, count]) => ({ car, count }));

  // console.log(top3Cars);

  const topBookedCars = () => {
    return allCarsList.slice(0, 3);
  };
  const fromfind = searchParams.get("find");
  console.log("fromfind", fromfind);

  useEffect(() => {
    initializeCars();
    fetchAvailabilityData();

    // --- Auto-refresh: poll every 60s + re-fetch on tab focus ---
    const pollInterval = setInterval(() => {
      refreshCars();
    }, 60000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshCars();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchAvailabilityData = async () => {
    try {
      const response = await axiosInstance.get("/cars/admin/availability", { timeout: 15000 });
      if (response.data && Array.isArray(response.data)) {
        setAvailabilityData(response.data);
        console.log('✅ Availability data fetched:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching availability data:', error);
    }
  };

  // Silent refresh — updates car data without showing skeleton
  const refreshCars = async () => {
    try {
      const startDate = userBooking.booking.pickUpDate
        ? moment(userBooking.booking.pickUpDate).format("YYYY-MM-DD")
        : null;
      const endDate = userBooking.booking.returnDate
        ? moment(userBooking.booking.returnDate).format("YYYY-MM-DD")
        : null;
      const res = await getAllCars(startDate, endDate);
      if (res && Array.isArray(res) && res.length > 0) {
        const startDate = moment(userBooking.booking.pickUpDate);
        const endDate = moment(userBooking.booking.returnDate);
        const daysDiff = endDate.diff(startDate, "days");
        const inclusiveDays = daysDiff === 0 ? 1 : daysDiff;

        const updatedCars = res.map((car) => {
          const rates = car.rates;
          const unlimited_rates = car.unlimedRates;
          const rate = rates.find(
            (r) => inclusiveDays >= r.minDays && (r.maxDays === null || inclusiveDays <= r.maxDays)
          );
          const unlimited_rate = unlimited_rates.find(
            (r) => inclusiveDays >= r.minDays && (r.maxDays === null || inclusiveDays <= r.maxDays)
          );
          if (rate) {
            car.defaultRatePerDay = fromfind == 1 ? rate.rate * inclusiveDays : rate.rate;
            car.displayRateLabel = rate.title || `${rate.minDays}+ Days Rate`;
            if (rate.originalRate) {
              car.originalRatePerDay = fromfind == 1 ? rate.originalRate * inclusiveDays : rate.originalRate;
            }
          }
          if (unlimited_rate) {
            car.defaultUnlimitedRatePerDay = fromfind == 1 ? unlimited_rate.rate * inclusiveDays : unlimited_rate.rate;
            car.displayUnlimitedRateLabel = unlimited_rate.title || `${unlimited_rate.minDays}+ Days Rate`;
            if (unlimited_rate.originalRate) {
              car.originalUnlimitedRatePerDay = fromfind == 1 ? unlimited_rate.originalRate * inclusiveDays : unlimited_rate.originalRate;
            }
          }
          const isUnavailable = car.availabilityStatus === 'unavailable';
          const isSoldOut = car.isFullyBooked || car.availableNow === 0;
          let unavailableLabel = null;
          if (isUnavailable) unavailableLabel = car.availabilityLabel || 'Coming Soon';
          else if (isSoldOut) unavailableLabel = 'Sold Out';
          return { ...car, overlappingCount: car.activeBookings || 0, isSold: isSoldOut, isUnavailable, unavailableLabel };
        });
        setAllCarsList(updatedCars);
        setCachedCars(updatedCars);
        console.log('🔄 Cars silently refreshed');
      }
    } catch (error) {
      // Silent fail — don't disrupt the user
      console.warn('Silent refresh failed:', error.message);
    }
  };

  const handleViewAvailability = (car) => {
    const carAvailability = availabilityData.find(
      (avail) => avail.carId === car._id
    );
    
    if (carAvailability && carAvailability.upcomingReturns) {
      setSelectedCarForAvailability({
        ...car,
        upcomingReturns: carAvailability.upcomingReturns,
        nextAvailableDate: carAvailability.nextAvailableDate
      });
      setShowAvailabilityModal(true);
    }
  };

  const initializeCars = async () => {
    // Only show skeleton if no cached data
    if (cachedCars.length === 0) {
      setLoading(true);
    }
    const updatedRoutes = [...visitedRoutes];
    updatedRoutes.push("car");
    setVisitedRoutes(updatedRoutes);
    const startDate = moment(userBooking.booking.pickUpDate);
    const endDate = moment(userBooking.booking.returnDate);
    userBooking.booking.driverAge = 21;
    // Calculate days - minimum 1 day for same-day rentals
    const daysDiff = endDate.diff(startDate, "days");
    const inclusiveDays = daysDiff === 0 ? 1 : daysDiff;
    // Cars API uses date range to compute availability for user's dates
    const startDateStr = userBooking.booking.pickUpDate
      ? moment(userBooking.booking.pickUpDate).format("YYYY-MM-DD")
      : null;
    const endDateStr = userBooking.booking.returnDate
      ? moment(userBooking.booking.returnDate).format("YYYY-MM-DD")
      : null;
    const res = await getAllCars(startDateStr, endDateStr);
    // Safety check: ensure res is an array before mapping
    if (res && Array.isArray(res) && res.length > 0) {
      const bookedCars = res.map((car) => {
        const rates = car.rates;
        const unlimited_rates = car.unlimedRates;
        // car.type === "limited"
        //   ? car.rates
        //   : car.unlimedRates;

        const rate = rates.find(
          (rate) =>
            inclusiveDays >= rate.minDays &&
            (rate.maxDays === null || inclusiveDays <= rate.maxDays)
        );
        const unlimited_rate = unlimited_rates.find(
          (rate) =>
            inclusiveDays >= rate.minDays &&
            (rate.maxDays === null || inclusiveDays <= rate.maxDays)
        );
        
        // Always calculate prices based on rate tiers
        if (rate) {
          car.defaultRatePerDay = fromfind == 1 ? rate.rate * inclusiveDays : rate.rate;
          car.displayRateLabel = rate.title || `${rate.minDays}+ Days Rate`;
          // Store original price if discount was applied
          if (rate.originalRate) {
            car.originalRatePerDay = fromfind == 1 ? rate.originalRate * inclusiveDays : rate.originalRate;
          }
        } else {
          // Fallback to default if no matching tier
          car.displayRateLabel = "Daily Rate";
        }
        
        if (unlimited_rate) {
          car.defaultUnlimitedRatePerDay = fromfind == 1 ? unlimited_rate.rate * inclusiveDays : unlimited_rate.rate;
          car.displayUnlimitedRateLabel = unlimited_rate.title || `${unlimited_rate.minDays}+ Days Rate`;
          // Store original price if discount was applied
          if (unlimited_rate.originalRate) {
            car.originalUnlimitedRatePerDay = fromfind == 1 ? unlimited_rate.originalRate * inclusiveDays : unlimited_rate.originalRate;
          }
        } else {
          // Fallback to default if no matching tier
          car.displayUnlimitedRateLabel = "Daily Rate";
        }

        console.log("userBooking", userBooking.booking);
        // Use server-provided availability instead of client-side overlap calculation
        const isUnavailable = car.availabilityStatus === 'unavailable';
        const isSoldOut = car.isFullyBooked || car.availableNow === 0;
        
        // Get the display label for unavailable cars
        let unavailableLabel = null;
        if (isUnavailable) {
          unavailableLabel = car.availabilityLabel || 'Coming Soon';
        } else if (isSoldOut) {
          unavailableLabel = 'Sold Out';
        }
        
        return {
          ...car,
          overlappingCount: car.activeBookings || 0,
          isSold: isSoldOut,
          isUnavailable,
          unavailableLabel,
        };
      });

      setAllCarsList(bookedCars);
      setCachedCars(bookedCars);
    }
    console.log("initializeCars ~ res:", res);
    setLoading(false);
  };

  const onSelectingCar = (car, type) => {
    let booking = { ...userBooking };
    booking.car = { ...car, type: type };
    
    // Store the EXACT price shown to user (this preserves discounts)
    const selectedPrice = type === "limited" 
      ? car.defaultRatePerDay 
      : car.defaultUnlimitedRatePerDay;
    
    // Store the rate tiers with discounts already applied
    booking.selectedRates = type === "limited" ? car.rates : car.unlimedRates;
    booking.selectedType = type;
    booking.hasDiscount = !!car.activeDiscount;
    
    console.log('🚗 Car Selected:', {
      car: car.name,
      type: type,
      displayPrice: selectedPrice,
      hasDiscount: booking.hasDiscount,
      ratesStored: booking.selectedRates?.length || 0
    });
    
    setUserBooking(booking);
    navigate(`/booking?id=${car._id}`);
  };

  // const getCarImg = (car) => {
  //   if (car.name === "Suzuki Swift") {
  //     return "img/cars/suzuki_swift.png";
  //   } else if (car.name === "Mazda CX3") {
  //     return "img/cars/mazda_cx3.png";
  //   }
  //   else if (car.name === "Mazda CX8") {
  //     return "img/cars/mazda_cx8.png";
  //   } else if (car.name === "Toyota Camry") {
  //     return "img/cars/toyota_camry.png";
  //   } else if (car.name === "Toyota Hilux") {
  //     return "img/cars/hilux.png";
  //   } else if (car.name === "Toyota Corolla") {
  //     return "img/cars/toyota_corrolla.png";
  //   } else if (car.name === "Outlander") {
  //     return "img/cars/mitsubishi_out.png";
  //   } else if (car.name === "Tesla Model 3") {
  //     return "img/cars/car_6.png";
  //   }
  // };

  // Show skeleton while loading
  if (loading) {
    return <SkeletonTopCars />;
  }

  return (
    <>
      <div class="CustomHeading1">
        {/* <h2 class="Head_1">TAK8, Just Make It!</h2> */}
        <h3 class="Head_2">
          <span class="colorRed">Explore</span> our{" "}
          <span class="colorYellow">best-selling</span> rental cars
        </h3>
        <div 
          className="Pra_1 rich-content"
          dangerouslySetInnerHTML={{ 
            __html: getSectionContent('top-cars', 'description', '') || 
                    getSectionContent('top-cars', 'content', 'chosen by customers for their comfort, reliability, and value!') 
          }}
        />
      </div>

      <section>
        <div class="container">
          {/* <Stepper /> */}
          <div class="row fade-in">
            {topBookedCars().map((car, index) => (
              <div class="col-md-4" key={car._id || index}>
                <div class={`CarBox ${car.isUnavailable ? 'car-unavailable' : ''}`}>
                  {/* Discount Banner - Keep this */}
                  {!car.isUnavailable && car.activeDiscount && (
                    <div className="discount-banner-header">
                      <span className="discount-name">{car.activeDiscount.name}</span>
                      <span className="discount-percentage">{car.activeDiscount.badge}</span>
                    </div>
                  )}
                  <div class="Image">
                    <img src={getCarImg(car)} alt="car" />
                    {car.isAutomatic ? (
                      <span class="Tag Red">Automatic</span>
                    ) : (
                      <span class="Tag Yellow">Manual</span>
                    )}
                    {/* <span class="Tag Red" onClick={() => setPopupOpen(true)}>Details</span> */}
                    <span class="Feature">
                      <img src="img/icon/carseat.svg" alt="Icon" /> {car.seats}{" "}
                      Seats |
                      <img src="img/icon/bags.svg" alt="Icon" /> {car.bags} Bags
                    </span>
                  </div>
                  <div class="Content">
                    <div class="Heading">
                      <h4 class="Head_3">{car.name}</h4>
                      {/* <img src="img/icon/carseat.svg" alt="Icon" /> {car.seats}{" "}
                      Seats
                      <img src="img/icon/bags.svg" alt="Icon" /> {car.bags} Bags */}
                      <div class="Features">
                        {/* <img src="img/icon/air_conditioning.svg" alt="Icon" />
                        <img src="img/icon/gps_navigation.svg" alt="Icon" />
                        <img src="img/icon/bluetooth.svg" alt="Icon" />
                        <img src="img/icon/camera.svg" alt="Icon" />
                        <img src="img/icon/cruise_control.svg" alt="Icon" /> */}
                      </div>
                    </div>
                    <div className="PriceMain">
                      <div class="Price">
                        <div>
                          {car.originalRatePerDay && !car.isUnavailable && (
                            <span className="savings">Save ${(car.originalRatePerDay - car.defaultRatePerDay).toFixed(2)}</span>
                          )}
                          {car.originalRatePerDay && !car.isUnavailable ? (
                            <>
                              <h5 className="original-price">${car.originalRatePerDay}</h5>
                              <h5 className="discounted-price">${car.defaultRatePerDay}</h5>
                            </>
                          ) : (
                            <h5>${car.defaultRatePerDay}</h5>
                          )}
                          <span className="rate-label">{car.displayRateLabel || "7+ Days Rate"}</span>
                        </div>
                        <p>150KM / Day</p>
                        <a
                          onClick={() => {
                            if (!car.isUnavailable && !car.isSold) {
                              onSelectingCar(car, "limited");
                            }
                          }}
                          class={`BtnBorderY cursor-pointer ${
                            (car.isSold || car.isUnavailable) ? "disabled-link" : ""
                          }`}
                        >
                          {car.unavailableLabel || (car.isSold ? "Sold Out" : "Select")}
                        </a>
                        {car.isSold && (
                          <span 
                            className="view-availability-link"
                            onClick={() => handleViewAvailability(car)}
                          >
                            View Availability
                          </span>
                        )}
                      </div>
                      <div class="Price">
                        <div>
                          {car.originalUnlimitedRatePerDay && !car.isUnavailable && (
                            <span className="savings">Save ${(car.originalUnlimitedRatePerDay - car.defaultUnlimitedRatePerDay).toFixed(2)}</span>
                          )}
                          {car.originalUnlimitedRatePerDay && !car.isUnavailable ? (
                            <>
                              <h5 className="original-price">${car.originalUnlimitedRatePerDay}</h5>
                              <h5 className="discounted-price">${car.defaultUnlimitedRatePerDay}</h5>
                            </>
                          ) : (
                            <h5>${car.defaultUnlimitedRatePerDay}</h5>
                          )}
                          <span className="rate-label">{car.displayUnlimitedRateLabel || "7+ Days Rate"}</span>
                        </div>

                        <p>Unlimited KM</p>
                        <a
                          onClick={() => {
                            if (!car.isUnavailable && !car.isSold) {
                              onSelectingCar(car, "unlimited");
                            }
                          }}
                          class={`BtnFill cursor-pointer ${
                            (car.isSold || car.isUnavailable) ? "disabled-link" : ""
                          }`}
                        >
                          {car.unavailableLabel || (car.isSold ? "Sold Out" : "Select")}
                        </a>
                        {car.isSold && (
                          <span 
                            className="view-availability-link"
                            onClick={() => handleViewAvailability(car)}
                          >
                            View Availability
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div class="row pt-4">
            <div class="Button text-center">
              <a href="#" onClick={() => routeTo("/carslist")} class="BtnFill">
                More Cars
              </a>
            </div>
          </div>
        </div>
      </section>
      
      <AvailabilityModal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        car={selectedCarForAvailability}
        upcomingReturns={selectedCarForAvailability?.upcomingReturns || []}
      />
    </>
  );
};

export default TopCars;
